import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { AuthProvider, useAuth } from './AuthContext';
import LoginModal from './components/LoginModal';
import WorkflowShell from './components/WorkflowShell';
import HomePage from './pages/HomePage';
import SeriesPage from './pages/SeriesPage';
import AddToShelfPage from './pages/AddToShelfPage';
import CreateProjectPage from './pages/CreateProjectPage';
import AddFromShelfPage from './pages/AddFromShelfPage';
import ProjectPage from './pages/ProjectPage';
import ArchivedProjectsPage from './pages/ArchivedProjectsPage';
import AdminPage from './pages/AdminPage';
import ReleaseNotesPage from './pages/ReleaseNotesPage';
import PlansPage from './pages/PlansPage';
import ExpressionEnginePage from './pages/ExpressionEnginePage';
import AnimationEnginePage from './pages/AnimationEnginePage';

// Use concrete hex for Ant contrast math — CSS vars alone make primary text unreadable
// (black-on-black) because Ant cannot derive colorTextLightSolid from var(--…).
const theme = {
  token: {
    fontFamily: 'var(--font-sans)',
    colorPrimary: '#111111',
    colorPrimaryHover: '#0a0a0a',
    colorPrimaryActive: '#0a0a0a',
    colorTextLightSolid: '#ffffff',
    borderRadius: 8,
    colorBgLayout: '#f6f7f9',
    colorText: '#242936',
    colorTextSecondary: '#4d5566',
    colorBorder: '#d8dce4',
    colorBgContainer: '#ffffff',
  },
  components: {
    Button: {
      primaryColor: '#ffffff',
      colorPrimary: '#111111',
      defaultColor: '#242936',
      defaultBg: '#ffffff',
      defaultBorderColor: '#d8dce4',
      /* Hover stays on the structural border. Ink is for primary fills, not an outline. */
      defaultHoverColor: '#242936',
      defaultHoverBorderColor: '#d8dce4',
      defaultActiveColor: '#242936',
      defaultActiveBorderColor: '#d8dce4',
      /* Depth (raised / depressed / pressed) lives in --button-shadow-* */
      defaultShadow: 'none',
      primaryShadow: 'none',
      dangerShadow: 'none',
    },
  },
};

function AppShell() {
  const { user, loading } = useAuth();

  if (loading || !user) {
    return <LoginModal />;
  }

  return (
    <Router>
      <Routes>
        <Route element={<WorkflowShell />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/series/:seriesId/add/:projectId" element={<AddToShelfPage />} />
          <Route path="/series/:seriesId" element={<SeriesPage />} />
          <Route path="/new" element={<CreateProjectPage />} />
          <Route path="/projects/:projectId/from-shelf" element={<AddFromShelfPage />} />
          <Route path="/archived" element={<ArchivedProjectsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/release-notes" element={<ReleaseNotesPage />} />
          <Route path="/plans" element={<PlansPage />} />
          <Route path="/expression-engine" element={<ExpressionEnginePage />} />
          <Route path="/animation-engine" element={<AnimationEnginePage />} />
          <Route path="/projects/:projectId/*" element={<ProjectPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <ConfigProvider theme={theme}>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;
